import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { partnerAdminApi } from "@/lib/partner";

function money(cents, cur) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: (cur || "USD").toUpperCase() })
      .format((Number(cents) || 0) / 100);
  } catch { return `${cents}`; }
}

const BLOCK_LABELS = {
  partner_suspended: "Partner suspended",
  partner_not_active: "Partner not active yet",
  payout_info_missing: "Payout info missing",
  below_minimum: "Below minimum payout",
};

export default function PartnerPayoutsAdmin() {
  const [plan, setPlan] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [detailFor, setDetailFor] = useState(null);
  const [markPaidFor, setMarkPaidFor] = useState(null);
  const [cancelFor, setCancelFor] = useState(null);
  const [externalRef, setExternalRef] = useState("");
  const [cancelReason, setCancelReason] = useState("");

  const loadPayouts = async () => {
    try {
      const r = await partnerAdminApi.listPayouts({ limit: 200 });
      setPayouts(r.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load payouts"); }
  };
  useEffect(() => { loadPayouts(); }, []);

  const runPlan = async () => {
    setBusy(true);
    try {
      const r = await partnerAdminApi.planPayouts({ as_of_date: asOf });
      setPlan(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Planning failed");
    } finally { setBusy(false); }
  };

  const commitOne = async (b) => {
    if (!b.eligible) return;
    if (!window.confirm(`Commit payout of ${money(b.amount_cents, b.currency)} for partner ${b.partner_code}?`)) return;
    try {
      await partnerAdminApi.commitPayout({
        partner_id: b.partner_id,
        currency: b.currency,
        amount_cents: b.amount_cents,
        commission_ids: b.commission_ids,
        method: b.payout_method,
        planned_for_period: b.planned_for_period,
      });
      toast.success("Payout committed");
      runPlan(); loadPayouts();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.detail || "Commit failed"));
    }
  };

  const submitMarkPaid = async () => {
    if (!externalRef.trim()) return toast.error("External ref is required");
    try {
      await partnerAdminApi.markPayoutPaid(markPaidFor, externalRef.trim());
      toast.success("Marked as paid");
      setMarkPaidFor(null); setExternalRef(""); loadPayouts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const submitCancel = async () => {
    try {
      await partnerAdminApi.cancelPayout(cancelFor, cancelReason);
      toast.success("Payout cancelled");
      setCancelFor(null); setCancelReason(""); loadPayouts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const openDetail = async (id) => {
    try {
      const r = await partnerAdminApi.payoutDetail(id);
      setDetailFor(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const eligibleTotal = useMemo(() =>
    (plan?.batches || []).filter((b) => b.eligible).reduce((s, b) => s + b.amount_cents, 0),
    [plan]
  );

  return (
    <div className="space-y-6" data-testid="admin-partner-payouts">
      <div className="rounded-2xl border border-white/10 bg-[#0B0D12] p-5">
        <p className="text-xs uppercase tracking-wider text-white/50">Monthly payout planner</p>
        <p className="mt-1 text-xs text-white/40">
          Run planning on the 1st of each month for the prior month&rsquo;s approved commissions.
          Planning is a PREVIEW — nothing is committed until you click Commit.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            data-testid="planner-asof"
            className="rounded-lg border border-white/15 bg-black/40 px-3 py-1.5 text-sm text-white"
          />
          <button
            onClick={runPlan}
            disabled={busy}
            data-testid="planner-run"
            className="rounded-lg bg-[#D6A653] px-3 py-1.5 text-xs text-black disabled:opacity-60"
          >
            {busy ? "Planning…" : "Run planning"}
          </button>
        </div>

        {plan && (
          <div className="mt-5">
            <p className="text-[11px] uppercase tracking-wider text-white/45">
              Preview · period {plan.planned_for_period} · eligible {money(eligibleTotal)} across {plan.totals.eligible_partners} partner(s)
            </p>
            <div className="mt-3 overflow-x-auto rounded-xl border border-white/10 bg-black/30">
              <table className="min-w-full text-xs">
                <thead className="text-left uppercase tracking-wider text-white/40">
                  <tr>
                    <th className="px-3 py-2">Partner</th>
                    <th className="px-3 py-2">Method</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-right">Commissions</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-white/80">
                  {(plan.batches || []).length === 0 ? (
                    <tr><td className="px-3 py-3 text-white/40" colSpan={6}>Nothing to plan.</td></tr>
                  ) : plan.batches.map((b) => (
                    <tr key={`${b.partner_id}:${b.currency}`} className="border-t border-white/5" data-testid={`plan-batch-${b.partner_id}`}>
                      <td className="px-3 py-2 font-mono text-[#D6A653]">{b.partner_code || b.partner_id.slice(0, 8)}</td>
                      <td className="px-3 py-2 text-white/60 capitalize">{(b.payout_method || "").replace("_", " ")}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(b.amount_cents, b.currency)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{b.commissions_count}</td>
                      <td className="px-3 py-2">
                        {b.eligible ? (
                          <span className="text-emerald-300">Eligible</span>
                        ) : (
                          <span className="text-amber-300">{(b.block_reasons || []).map((r) => BLOCK_LABELS[r] || r).join(", ")}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {b.eligible && (
                          <button
                            onClick={() => commitOne(b)}
                            data-testid={`commit-batch-${b.partner_id}`}
                            className="rounded-lg border border-emerald-500/40 px-2.5 py-1 text-xs text-emerald-300 hover:bg-emerald-500/10"
                          >
                            Commit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Existing payouts */}
      <div className="rounded-2xl border border-white/10 bg-[#0B0D12] p-5">
        <p className="text-xs uppercase tracking-wider text-white/50">Committed payouts</p>
        {payouts.length === 0 ? (
          <p className="mt-3 text-sm text-white/50" data-testid="admin-no-payouts">No payouts committed yet.</p>
        ) : (
          <div className="mt-3 overflow-x-auto rounded-xl border border-white/10 bg-black/30">
            <table className="min-w-full text-xs">
              <thead className="text-left uppercase tracking-wider text-white/40">
                <tr>
                  <th className="px-3 py-2">Period</th>
                  <th className="px-3 py-2">Partner</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2">External ref</th>
                  <th className="px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="text-white/80">
                {payouts.map((p) => (
                  <tr key={p.id} className="border-t border-white/5" data-testid={`admin-payout-${p.id}`}>
                    <td className="px-3 py-2 font-mono">{p.planned_for_period}</td>
                    <td className="px-3 py-2 text-white/60">{p.partner_id.slice(0, 8)}</td>
                    <td className="px-3 py-2 capitalize">{p.status}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(p.amount_cents, p.currency)}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-white/50">{p.external_ref || "—"}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => openDetail(p.id)} data-testid={`admin-payout-detail-${p.id}`}
                                className="rounded border border-white/15 px-2 py-0.5 text-[11px] text-white/70 hover:bg-white/5">
                          Detail
                        </button>
                        {p.status === "queued" && (
                          <>
                            <button onClick={() => setMarkPaidFor(p.id)} data-testid={`admin-mark-paid-${p.id}`}
                                    className="rounded border border-emerald-500/40 px-2 py-0.5 text-[11px] text-emerald-300 hover:bg-emerald-500/10">
                              Mark paid
                            </button>
                            <button onClick={() => setCancelFor(p.id)} data-testid={`admin-cancel-${p.id}`}
                                    className="rounded border border-red-500/40 px-2 py-0.5 text-[11px] text-red-300 hover:bg-red-500/10">
                              Cancel
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mark paid modal */}
      {markPaidFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="mark-paid-modal">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0B0D12] p-6">
            <h3 className="text-lg font-light text-white">Mark payout as paid</h3>
            <p className="mt-1 text-xs text-white/50">
              Enter the external reference (bank transfer id, PayPal payout id, etc.). This is stored on the payout row and
              exported on the statement CSV.
            </p>
            <input
              value={externalRef}
              onChange={(e) => setExternalRef(e.target.value)}
              placeholder="e.g. bank-txn-12345"
              data-testid="mark-paid-ref"
              className="mt-4 w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white"
            />
            <div className="mt-4 flex justify-end gap-3">
              <button onClick={() => { setMarkPaidFor(null); setExternalRef(""); }}
                      className="rounded-lg border border-white/15 px-4 py-2 text-sm text-white/70">
                Cancel
              </button>
              <button onClick={submitMarkPaid} data-testid="mark-paid-submit"
                      className="rounded-lg bg-[#D6A653] px-4 py-2 text-sm text-black">
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel modal */}
      {cancelFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="cancel-payout-modal">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0B0D12] p-6">
            <h3 className="text-lg font-light text-white">Cancel payout</h3>
            <p className="mt-1 text-xs text-white/50">Reverts every commission in this batch back to `approved`. Cannot be undone.</p>
            <input
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason (visible in ledger)"
              data-testid="cancel-reason"
              className="mt-4 w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white"
            />
            <div className="mt-4 flex justify-end gap-3">
              <button onClick={() => { setCancelFor(null); setCancelReason(""); }}
                      className="rounded-lg border border-white/15 px-4 py-2 text-sm text-white/70">
                Never mind
              </button>
              <button onClick={submitCancel} data-testid="cancel-payout-submit"
                      className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-300">
                Cancel payout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detailFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="payout-detail-modal">
          <div className="w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0B0D12] p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-light text-white">Payout detail</h3>
                <p className="mt-1 text-xs text-white/50">Partner <span className="font-mono">{detailFor.partner?.partner_code || detailFor.payout.partner_id}</span> · period {detailFor.payout.planned_for_period}</p>
              </div>
              <button onClick={() => setDetailFor(null)} className="text-white/60 hover:text-white">✕</button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
              <Stat label="Status" value={detailFor.payout.status} />
              <Stat label="Amount" value={money(detailFor.payout.amount_cents, detailFor.payout.currency)} />
              <Stat label="Method" value={detailFor.payout.method} />
              <Stat label="External ref" value={detailFor.payout.external_ref || "—"} />
              <Stat label="Queued" value={detailFor.payout.queued_at || "—"} />
              <Stat label="Paid" value={detailFor.payout.paid_at || "—"} />
            </div>
            <div className="mt-5">
              <p className="text-[11px] uppercase tracking-wider text-white/45">Commissions in this payout</p>
              <div className="mt-2 overflow-x-auto rounded-xl border border-white/10 bg-black/40">
                <table className="min-w-full text-xs">
                  <thead className="text-left uppercase tracking-wider text-white/40">
                    <tr>
                      <th className="px-3 py-2">Invoice</th>
                      <th className="px-3 py-2">Source</th>
                      <th className="px-3 py-2 text-right">Cents</th>
                      <th className="px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="text-white/80">
                    {(detailFor.commissions || []).map((c) => (
                      <tr key={c.id} className="border-t border-white/5">
                        <td className="px-3 py-2 font-mono text-white/60">{c.stripe_invoice_id?.slice(0, 20)}</td>
                        <td className="px-3 py-2">{c.attribution_source}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{money(c.commission_cents, c.currency)}</td>
                        <td className="px-3 py-2 capitalize">{c.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-lg border border-white/10 bg-black/30 px-3 py-2">
    <p className="text-[10px] uppercase tracking-wider text-white/40">{label}</p>
    <p className="mt-0.5 truncate text-white/80">{value}</p>
  </div>
);
